/*
  ЦИФРОВОЙ ДИАК ЗФ — MEMOS PAGE («Журнал встреч»)

  Список протоколов встреч + редактор с 7 блоками и LLM-автозаполнением из
  транскрипта. Полностью изолировано от ProjectContext (своё состояние,
  свои API-вызовы) — не влияет на канбан и его realtime.
*/

import { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "@/components/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  CalendarDays,
  Plus,
  Loader2,
  Trash2,
  Sparkles,
  ArrowLeft,
  FileText,
  Presentation,
  Users,
  Printer,
  Pencil,
  Lightbulb,
  ArrowUpNarrowWide,
  ArrowDownWideNarrow,
} from "lucide-react";
import {
  listMemos,
  getMemo,
  createMemo,
  updateMemo,
  deleteMemo,
  draftMemoFromTranscript,
  EMPTY_SECTIONS,
  type Memo,
  type MemoListItem,
  type MemoType,
  type MemoSections,
} from "@/lib/memosApi";

const TYPE_META: Record<MemoType, { label: string; color: string; icon: typeof FileText }> = {
  planning: { label: "Планирование", color: "#0055A5", icon: CalendarDays },
  demo: { label: "Демо", color: "#00A651", icon: Presentation },
  brainstorm: { label: "Мозговой штурм", color: "#D97706", icon: Lightbulb },
  other: { label: "Рабочая", color: "#6B7280", icon: FileText },
};

const TYPE_ORDER: MemoType[] = ["planning", "demo", "brainstorm", "other"];

const SECTION_DEFS: Array<{ key: keyof MemoSections; label: string; hint: string }> = [
  { key: "summary", label: "Резюме", hint: "О чём встреча и главный итог (2–4 предложения)" },
  { key: "organization", label: "Организация / процесс", hint: "Договорённости и изменения формата работы" },
  { key: "directions", label: "Статус по направлениям / Что показали", hint: "По каждому треку: сделано / в работе / дальше" },
  { key: "decisions", label: "Принятые решения", hint: "Список ключевых решений" },
  { key: "actionItems", label: "Поручения", hint: "Что · ответственный · срок" },
  { key: "risks", label: "Риски, блокеры, нужно от руководства", hint: "Что мешает и что требуется сверху" },
  { key: "milestones", label: "Дедлайны и контрольные точки", hint: "Даты и вехи" },
];

function fmtDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return new Date(`${m[1]}-${m[2]}-${m[3]}`).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function todayInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function TypeBadge({ type }: { type: MemoType }) {
  const meta = TYPE_META[type];
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
      style={{ backgroundColor: meta.color }}
    >
      <Icon size={11} /> {meta.label}
    </span>
  );
}

export default function MemosPage() {
  const { canEdit, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [memos, setMemos] = useState<MemoListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [active, setActive] = useState<Memo | null>(null);
  const [loadingMemo, setLoadingMemo] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Фильтр по типу встречи и направление сортировки по дате.
  // По умолчанию — строгая хронология: от старых к новым.
  const [typeFilter, setTypeFilter] = useState<MemoType | "all">("all");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const typeCounts = useMemo(() => {
    const counts = { planning: 0, demo: 0, brainstorm: 0, other: 0 } as Record<MemoType, number>;
    for (const m of memos) counts[m.type] = (counts[m.type] ?? 0) + 1;
    return counts;
  }, [memos]);

  const visibleMemos = useMemo(() => {
    const filtered = typeFilter === "all" ? memos : memos.filter((m) => m.type === typeFilter);
    // meetingDate — ISO (YYYY-MM-DD), поэтому строкового сравнения достаточно;
    // при равных датах порядок стабилизируем по createdAt.
    return [...filtered].sort((a, b) => {
      const cmp =
        a.meetingDate.localeCompare(b.meetingDate) || a.createdAt.localeCompare(b.createdAt);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [memos, typeFilter, sortDir]);

  const removeMemo = useCallback(async (m: MemoListItem) => {
    if (typeof window !== "undefined") {
      if (!window.confirm(`Удалить мемо «${m.title}» от ${fmtDate(m.meetingDate)}? Действие необратимо.`)) {
        return;
      }
    }
    setDeletingId(m.id);
    try {
      await deleteMemo(m.id);
      setMemos((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Мемо удалено");
    } catch {
      toast.error("Не удалось удалить мемо");
    } finally {
      setDeletingId(null);
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      setMemos(await listMemos());
    } catch {
      toast.error("Не удалось загрузить журнал встреч");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  const openMemo = async (id: string) => {
    setLoadingMemo(true);
    try {
      setActive(await getMemo(id));
    } catch {
      toast.error("Не удалось открыть мемо");
    } finally {
      setLoadingMemo(false);
    }
  };

  if (active || loadingMemo) {
    return (
      <MemoEditor
        memo={active}
        loading={loadingMemo}
        canEdit={canEdit}
        onBack={() => {
          setActive(null);
          void refreshList();
        }}
        onSaved={(m) => setActive(m)}
        onDeleted={() => {
          setActive(null);
          void refreshList();
        }}
      />
    );
  }

  return (
    <div className="flex min-h-screen" style={{ background: "var(--background)" }}>
      <Sidebar />
      <main className="relative min-h-screen flex-1 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-white px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "var(--nn-dark)" }}>
              Журнал встреч
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Протоколы планирований и демо. Можно заполнить вручную или автоматически из транскрипта.
            </p>
          </div>
          {canEdit ? (
            <NewMemoButton
              onCreated={(m) => {
                void refreshList();
                setActive(m);
              }}
            />
          ) : null}
        </header>

        <div className="p-6">
          {!loadingList && memos.length > 0 ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setTypeFilter("all")}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  typeFilter === "all"
                    ? "border-transparent bg-[var(--nn-dark)] text-white"
                    : "border-border bg-card text-muted-foreground hover:bg-muted/50",
                )}
              >
                Все ({memos.length})
              </button>
              {TYPE_ORDER.filter((t) => typeCounts[t] > 0).map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const isActive = typeFilter === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTypeFilter(isActive ? "all" : t)}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                      isActive
                        ? "border-transparent text-white"
                        : "border-border bg-card text-muted-foreground hover:bg-muted/50",
                    )}
                    style={isActive ? { backgroundColor: meta.color } : undefined}
                  >
                    <Icon size={12} /> {meta.label} ({typeCounts[t]})
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50"
                title="Переключить порядок сортировки по дате встречи"
              >
                {sortDir === "asc" ? (
                  <ArrowUpNarrowWide size={14} />
                ) : (
                  <ArrowDownWideNarrow size={14} />
                )}
                {sortDir === "asc" ? "Сначала старые" : "Сначала новые"}
              </button>
            </div>
          ) : null}

          {loadingList ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Загрузка…
            </div>
          ) : memos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/20 p-10 text-center text-sm text-muted-foreground">
              Пока нет ни одной записи. Нажмите «Новая встреча», чтобы создать первый протокол.
            </div>
          ) : visibleMemos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/20 p-10 text-center text-sm text-muted-foreground">
              Нет встреч выбранного типа.
            </div>
          ) : (
            <ul className="space-y-2">
              {visibleMemos.map((m) => (
                <li
                  key={m.id}
                  className="group flex items-center gap-1 rounded-lg border border-border bg-card pr-2 shadow-sm transition-colors hover:border-[var(--nn-blue)]/50 hover:bg-[var(--nn-blue-pale)]/20"
                >
                  <button
                    type="button"
                    onClick={() => void openMemo(m.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left"
                  >
                    <TypeBadge type={m.type} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{m.title}</span>
                      {m.participants ? (
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {m.participants}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
                      {fmtDate(m.meetingDate)}
                    </span>
                  </button>
                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={() => void removeMemo(m)}
                      disabled={deletingId === m.id}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground/50 transition-all hover:bg-red-50 hover:text-red-600 disabled:opacity-40 sm:opacity-0 sm:group-hover:opacity-100"
                      aria-label="Удалить мемо"
                      title="Удалить мемо"
                    >
                      {deletingId === m.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}

function NewMemoButton({ onCreated }: { onCreated: (m: Memo) => void }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<MemoType>("planning");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(todayInput());
  const [busy, setBusy] = useState(false);

  const create = async () => {
    const t = title.trim() || `${TYPE_META[type].label} ${fmtDate(date)}`;
    setBusy(true);
    try {
      const memo = await createMemo({ type, title: t, meetingDate: date });
      onCreated(memo);
      setOpen(false);
      setTitle("");
    } catch {
      toast.error("Не удалось создать мемо");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[var(--nn-blue-dark)]"
      >
        <Plus size={16} /> Новая встреча
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-3 shadow-sm">
      <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span>Тип</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as MemoType)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        >
          <option value="planning">Планирование</option>
          <option value="demo">Демо</option>
          <option value="brainstorm">Мозговой штурм</option>
          <option value="other">Рабочая</option>
        </select>
      </label>
      <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span>Дата</span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        />
      </label>
      <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span>Название</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={`${TYPE_META[type].label} ${fmtDate(date)}`}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        />
      </label>
      <button
        type="button"
        onClick={() => void create()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={16} />}
        Создать
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50"
      >
        Отмена
      </button>
    </div>
  );
}

/** Мини-рендер Markdown-подмножества для итогового вида мемо: списки «- »,
 *  жирный **...**, абзацы. Без dangerouslySetInnerHTML — собираем React-узлы. */
function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<strong key={`b${i++}`}>{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function MemoMarkdown({ text }: { text: string }) {
  const lines = text.replace(/\r/g, "").split("\n");
  const blocks: React.ReactNode[] = [];
  let list: string[] = [];
  let para: string[] = [];
  const flushList = () => {
    if (list.length) {
      blocks.push(
        <ul key={`ul${blocks.length}`} className="my-1 ml-5 list-disc space-y-1">
          {list.map((li, i) => (
            <li key={i}>{renderInline(li)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };
  const flushPara = () => {
    if (para.length) {
      blocks.push(
        <p key={`p${blocks.length}`} className="my-1 leading-relaxed">
          {renderInline(para.join(" "))}
        </p>,
      );
      para = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    const bullet = /^([-*•])\s+(.*)$/.exec(line);
    if (bullet) {
      flushPara();
      list.push(bullet[2]);
    } else if (line === "") {
      flushList();
      flushPara();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushList();
  flushPara();
  return <div className="text-sm text-foreground/90">{blocks}</div>;
}

function MemoEditor({
  memo,
  loading,
  canEdit,
  onBack,
  onSaved,
  onDeleted,
}: {
  memo: Memo | null;
  loading: boolean;
  canEdit: boolean;
  onBack: () => void;
  onSaved: (m: Memo) => void;
  onDeleted: () => void;
}) {
  const [draft, setDraft] = useState<Memo | null>(memo);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Режим: 'view' — готовый документ (по умолчанию), 'edit' — редактирование.
  // Пустое мемо (только что создано) сразу открываем на редактирование.
  const [mode, setMode] = useState<"view" | "edit">("view");

  useEffect(() => {
    setDraft(memo);
    setDirty(false);
    if (memo) {
      const empty =
        !memo.transcript &&
        Object.values(memo.sections).every((v) => !v.trim());
      setMode(empty && canEdit ? "edit" : "view");
    }
  }, [memo, canEdit]);

  if (loading || !draft) {
    return (
      <div className="flex min-h-screen" style={{ background: "var(--background)" }}>
        <Sidebar />
        <main className="relative min-h-screen flex-1 [margin-left:var(--app-sidebar-width)]">
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Загрузка…
          </div>
        </main>
      </div>
    );
  }

  const patch = (p: Partial<Memo>) => {
    setDraft((prev) => (prev ? { ...prev, ...p } : prev));
    setDirty(true);
  };
  const patchSection = (key: keyof MemoSections, value: string) => {
    setDraft((prev) =>
      prev ? { ...prev, sections: { ...prev.sections, [key]: value } } : prev,
    );
    setDirty(true);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const saved = await updateMemo(draft.id, {
        type: draft.type,
        title: draft.title,
        meetingDate: draft.meetingDate,
        participants: draft.participants,
        transcript: draft.transcript,
        sections: draft.sections,
      });
      onSaved(saved);
      setDirty(false);
      setMode("view");
      toast.success("Сохранено");
    } catch {
      toast.error("Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const runDraft = async () => {
    if (!draft) return;
    const transcript = (draft.transcript || "").trim();
    if (transcript.length < 20) {
      toast.error("Вставьте транскрипт встречи в поле ниже (минимум пару абзацев).");
      return;
    }
    const hasContent = Object.values(draft.sections).some((v) => v.trim().length > 0);
    if (hasContent && typeof window !== "undefined") {
      if (!window.confirm("Заполнить блоки из транскрипта? Текущее содержимое блоков будет перезаписано.")) {
        return;
      }
    }
    setDrafting(true);
    try {
      const { sections } = await draftMemoFromTranscript(draft.type, transcript, draft.meetingDate);
      setDraft((prev) => (prev ? { ...prev, sections } : prev));
      setDirty(true);
      toast.success("Черновик сформирован. Проверьте и поправьте, затем сохраните.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "LLM не смогла сформировать черновик");
    } finally {
      setDrafting(false);
    }
  };

  const remove = async () => {
    if (!draft) return;
    if (typeof window !== "undefined" && !window.confirm(`Удалить мемо «${draft.title}»? Действие необратимо.`)) {
      return;
    }
    try {
      await deleteMemo(draft.id);
      toast.success("Мемо удалено");
      onDeleted();
    } catch {
      toast.error("Не удалось удалить");
    }
  };

  const typeMeta = TYPE_META[draft.type];
  const filledSections = SECTION_DEFS.filter((d) => draft.sections[d.key].trim().length > 0);

  return (
    <div className="flex min-h-screen" style={{ background: "var(--background)" }}>
      <Sidebar />
      <main className="relative min-h-screen flex-1 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
        {/* Header (скрывается при печати) */}
        <header className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-border bg-white px-6 py-3 print:hidden">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
          >
            <ArrowLeft size={16} /> К списку
          </button>

          {mode === "edit" ? (
            <div className="flex items-center gap-2">
              <select
                value={draft.type}
                onChange={(e) => patch({ type: e.target.value as MemoType })}
                className="rounded-md border border-border bg-background px-2 py-1 text-sm"
              >
                <option value="planning">Планирование</option>
                <option value="demo">Демо</option>
                <option value="brainstorm">Мозговой штурм</option>
                <option value="other">Рабочая</option>
              </select>
              <input
                type="date"
                value={draft.meetingDate.slice(0, 10)}
                onChange={(e) => patch({ meetingDate: e.target.value })}
                className="rounded-md border border-border bg-background px-2 py-1 text-sm"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <TypeBadge type={draft.type} />
              <span className="text-sm text-muted-foreground">{fmtDate(draft.meetingDate)}</span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            {mode === "view" ? (
              <>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
                  title="Печать / Сохранить в PDF"
                >
                  <Printer size={14} /> Печать
                </button>
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => setMode("edit")}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-1.5 text-sm font-medium text-white shadow-sm"
                  >
                    <Pencil size={14} /> Редактировать
                  </button>
                ) : null}
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-1.5 text-sm font-medium text-white shadow-sm disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Готово
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDraft(memo);
                    setDirty(false);
                    setMode("view");
                  }}
                  className="rounded-md px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={() => void remove()}
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-red-700 hover:bg-red-50"
                >
                  <Trash2 size={14} /> Удалить
                </button>
              </>
            )}
          </div>
        </header>

        {mode === "view" ? (
          /* ─── ИТОГОВЫЙ ДОКУМЕНТ ─── */
          <article className="mx-auto max-w-3xl px-8 py-8">
            <div className="mb-1 flex items-center gap-2 print:mb-2">
              <TypeBadge type={draft.type} />
              <span className="text-sm text-muted-foreground">{fmtDate(draft.meetingDate)}</span>
            </div>
            <h1 className="text-3xl font-bold leading-tight" style={{ color: "var(--nn-dark)" }}>
              {draft.title}
            </h1>
            {draft.participants ? (
              <p className="mt-2 text-sm text-muted-foreground">
                <span className="font-semibold">Участники:</span> {draft.participants}
              </p>
            ) : null}
            <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground/70">
              Программа «Цифровой ДИАК ЗФ» · протокол {typeMeta.label.toLowerCase()}
            </div>

            <hr className="my-5 border-border" />

            {filledSections.length === 0 ? (
              <p className="text-sm italic text-muted-foreground">
                Протокол пока пустой. {canEdit ? "Нажмите «Редактировать», чтобы заполнить вручную или из транскрипта." : ""}
              </p>
            ) : (
              <div className="space-y-5">
                {filledSections.map((def) => (
                  <section key={def.key} className="break-inside-avoid">
                    <h2
                      className="mb-1.5 border-l-[3px] pl-2 text-base font-bold uppercase tracking-wide"
                      style={{ borderColor: "var(--nn-blue)", color: "var(--nn-dark)" }}
                    >
                      {def.label}
                    </h2>
                    <MemoMarkdown text={draft.sections[def.key]} />
                  </section>
                ))}
              </div>
            )}
          </article>
        ) : (
          /* ─── РЕДАКТИРОВАНИЕ ─── */
          <div className="mx-auto max-w-4xl space-y-4 p-6">
            <input
              type="text"
              value={draft.title}
              onChange={(e) => patch({ title: e.target.value })}
              placeholder="Название встречи"
              className="w-full border-0 bg-transparent text-2xl font-bold outline-none placeholder:text-muted-foreground/50"
              style={{ color: "var(--nn-dark)" }}
            />
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users size={14} />
              <input
                type="text"
                value={draft.participants}
                onChange={(e) => patch({ participants: e.target.value })}
                placeholder="Участники (через запятую)"
                className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm"
              />
            </label>

            <div className="rounded-lg border border-[var(--nn-blue)]/30 bg-[var(--nn-blue-pale)]/20 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--nn-blue)]">
                  <Sparkles size={13} /> Автозаполнение из транскрипта
                </span>
                <button
                  type="button"
                  onClick={() => void runDraft()}
                  disabled={drafting}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-2.5 py-1 text-[12px] font-medium text-white disabled:opacity-60"
                >
                  {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles size={13} />}
                  {drafting ? "Обрабатываю…" : "Заполнить блоки"}
                </button>
              </div>
              <textarea
                value={draft.transcript}
                onChange={(e) => patch({ transcript: e.target.value })}
                rows={5}
                placeholder="Вставьте сюда транскрипт встречи. Нажмите «Заполнить блоки» — модель составит протокол, затем сама перепроверит его по транскрипту и исправит неточности. Это занимает до ~2 минут. Затем проверьте и поправьте при необходимости."
                className="w-full resize-y rounded-md border border-border bg-background px-2.5 py-2 text-[12px] leading-relaxed"
              />
            </div>

            {SECTION_DEFS.map((def) => (
              <section key={def.key} className="rounded-lg border border-border bg-card p-4 shadow-sm">
                <h3 className="mb-1 text-sm font-bold" style={{ color: "var(--nn-dark)" }}>
                  {def.label}
                </h3>
                <p className="mb-2 text-[11px] text-muted-foreground">{def.hint}</p>
                <textarea
                  value={draft.sections[def.key]}
                  onChange={(e) => patchSection(def.key, e.target.value)}
                  rows={Math.max(3, Math.min(14, draft.sections[def.key].split("\n").length + 1))}
                  placeholder="—"
                  className="w-full resize-y rounded-md border border-border bg-background px-2.5 py-2 text-sm leading-relaxed"
                />
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
